/* React */
import React, { useState } from "react";

/* Ionic */
import { IonTextarea } from "@ionic/react";
import { send } from "ionicons/icons";

/* Components */
import PrimaryButton from "src/components/shared/buttons/primary-button/PrimaryButton";

/* Stylesheet */
import styles from "./ChatInput.module.scss";

interface ChatInputProps {
  disabled: boolean;
  onSend: (question: string) => void;
  /**
   * What to invite, which differs by where this sits. The chat page
   * answers questions about the data; the assistant panel configures
   * the dashboard and cannot answer with numbers, so inviting "a
   * question about your data" there sets the wrong expectation.
   */
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  disabled,
  onSend,
  placeholder = "Ask a question about your data",
}) => {
  const [question, setQuestion] = useState<string>("");

  const handleSend = () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || disabled) {
      return;
    }
    onSend(trimmedQuestion);
    setQuestion("");
  };

  return (
    <div className={styles.chat_input}>
      <IonTextarea
        data-testid="chat-input"
        className={styles.textarea}
        placeholder={placeholder}
        value={question}
        autoGrow={true}
        rows={1}
        disabled={disabled}
        onIonInput={(event) =>
          setQuestion(event.detail.value ?? "")
        }
        onKeyDown={(event) => {
          /* isComposing: Enter confirms the current character for IME
             users, and must not also send a half-composed question. */
          /* repeat: holding Enter fires further keydowns before the
             parent has disabled the input, resending the same prompt. */
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.repeat &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            handleSend();
          }
        }}
      />
      <PrimaryButton
        testId="chat-send-button"
        type="button"
        ariaLabel="Send question"
        icon={send}
        label="Send"
        disabled={disabled || !question.trim()}
        onClick={handleSend}
      />
    </div>
  );
};

export default ChatInput;
